// Mirror of EuropassPdfHeader.jsx: a full-bleed grey identity band at the very
// top of the page containing the name (left) + Europass logo (top-right), a grey
// rule, and the contact-info line. The PDF band bleeds to the page edges, so the
// table is the full page width with a negative left indent that cancels the page
// margin.

import { AlignmentType, BorderStyle, ImageRun, Table, TableCell, TableRow, VerticalAlign, WidthType } from "docx";
import { getInitials } from "../../../../utils/getInitials";
import { alcyoneContactInformation, lusisContactInformation } from "../../../../utils/contactInformation";
import { NO_CELL_BORDERS, NO_TABLE_BORDERS, layoutTable, para, run } from "../../shared/wordHelpers";
import { EUROPASS_HEADER_BG, EUROPASS_RULE, EUROPASS_USER, FONT_VERDANA } from "../../shared/wordTheme";
import { A4, pxToTwips } from "../../shared/wordUnits";
import { EU_MARGIN } from "../euLayout";

export function buildEuropassBand( { data, props, translations, logoImage } ) {
    const { user } = data;
    const { isAnonymous, isAlcyone } = props;
    const t = translations?.pdf ?? {};

    const name = isAnonymous
        ? `${ getInitials( user?.firstName ) } ${ getInitials( user?.lastName, true ) }`
        : `${ user?.firstName ?? "" } ${ user?.lastName ?? "" }`;

    const contact = isAlcyone ? alcyoneContactInformation : lusisContactInformation;
    const item = ( labelText, value ) => [
        run( `${ labelText } `, { pt: 8, bold: true, font: FONT_VERDANA } ),
        run( `${ value }    `, { pt: 8, font: FONT_VERDANA } ),
    ];
    const contactRuns = [
        ...item( t.phoneNumberLabel, contact.phoneNumber ),
        ...item( t.emailAddressLabel, contact.emailAddress ),
        ...item( t.webSiteLabel, contact.webSite ),
        ...item( t.postalAddressLabel, contact.postalAddress ),
    ];

    // Inner row: name (left) + Europass logo (right).
    const innerWidth = A4.width - pxToTwips( 40 );
    const nameW = Math.round( innerWidth * 0.65 );
    const logoW = innerWidth - nameW;
    const namePara = para( run( name, { pt: 16, bold: true, color: EUROPASS_USER, font: FONT_VERDANA } ), { spacing: { after: 0 } } );
    const logoPara = logoImage
        ? para( new ImageRun( { data: logoImage.data, transformation: { width: 120, height: 24 } } ), { alignment: AlignmentType.RIGHT, spacing: { after: 0 } } )
        : para( [], { spacing: { after: 0 } } );
    const nameLogoRow = layoutTable(
        [
            new TableRow( {
                children: [
                    new TableCell( { width: { size: nameW, type: WidthType.DXA }, borders: NO_CELL_BORDERS, verticalAlign: VerticalAlign.CENTER, children: [ namePara ] } ),
                    new TableCell( { width: { size: logoW, type: WidthType.DXA }, borders: NO_CELL_BORDERS, verticalAlign: VerticalAlign.TOP, children: [ logoPara ] } ),
                ],
            } ),
        ],
        { width: innerWidth, columnWidths: [ nameW, logoW ] }
    );

    const cellChildren = [
        nameLogoRow,
        para( [], { border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: EUROPASS_RULE } }, spacing: { before: pxToTwips( 8 ), after: pxToTwips( 5 ) } } ),
        para( contactRuns, { spacing: { after: 0 } } ),
    ];

    // Full-bleed: full page width, negative indent cancels the page margin.
    return new Table( {
        width: { size: A4.width, type: WidthType.DXA },
        columnWidths: [ A4.width ],
        indent: { size: -EU_MARGIN, type: WidthType.DXA },
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow( {
                children: [
                    new TableCell( {
                        width: { size: A4.width, type: WidthType.DXA },
                        borders: NO_CELL_BORDERS,
                        verticalAlign: VerticalAlign.CENTER,
                        shading: { fill: EUROPASS_HEADER_BG },
                        margins: { left: pxToTwips( 20 ), right: pxToTwips( 20 ), top: pxToTwips( 16 ), bottom: pxToTwips( 16 ) },
                        children: cellChildren,
                    } ),
                ],
            } ),
        ],
    } );
}
